import React, { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import axios from 'axios';
import '../../styles/LeadCommissionChecklist.css';

const money = value => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));
const roleDetails = {
  implementation: { title: 'Implantação e sucesso do cliente', subtitle: 'Responsável pela implantação' },
  seller: { title: 'Acompanhamento comercial', subtitle: 'Vendedor responsável' },
};
const statusLabels = {
  blocked: 'Aguardando requisitos', available: 'Disponível para aprovação', approved: 'Comissão aprovada', paid: 'Comissão paga', reversed: 'Estornada',
};
const paymentLabels = {
  paid: 'Mensalidade paga', failed: 'Pagamento falhou', pending: 'Pagamento pendente', scheduled: 'Cobrança agendada', canceled: 'Fatura cancelada',
};

export default function LeadCommissionChecklist({ leadId, showAlertModal }) {
  const [data, setData] = useState({ contract: null, items: [], commissions: [], billing_cycles: [] });
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/sales/leads/${leadId}/commission-checklist`);
      setData(response.data);
    } catch (error) {
      showAlertModal('Erro', error.response?.data?.message || 'Não foi possível carregar o checklist.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (leadId) load(); }, [leadId]);

  const groups = useMemo(() => ['implementation', 'seller'].map(role => ({
    role,
    milestones: [1, 2, 3].map(milestone => ({
      milestone,
      items: data.items.filter(item => item.role === role && Number(item.milestone) === milestone),
      commission: data.commissions.find(item => item.type === (role === 'implementation' ? 'implementation' : 'sale') && Number(item.installment) === milestone),
    })).filter(group => group.items.length),
  })).filter(group => group.milestones.length), [data]);

  const toggle = async item => {
    setUpdatingId(item.id);
    try {
      const response = await axios.patch(`/api/sales/checklist-items/${item.id}`, { completed: !item.completed_at });
      setData(response.data);
    } catch (error) {
      showAlertModal('Não foi possível atualizar', error.response?.data?.message || 'Tente novamente.', 'warning');
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) return <div className="commission-checklist-loading"><Spinner animation="border" size="sm" /> Carregando checklist...</div>;
  if (!data.contract) return <div className="commission-checklist-empty"><strong>A contratação ainda não foi iniciada.</strong><span>Gere o link de contratação para disponibilizar os checklists da equipe.</span></div>;
  if (!groups.length) return <div className="commission-checklist-empty"><strong>Nenhum checklist atribuído ao seu usuário.</strong><span>Somente o vendedor, o implementador e o Representante Legal podem acessar os respectivos itens.</span></div>;

  return <div className="commission-checklist">
    <div className="commission-checklist-heading">
      <div><span>Jornada pós-venda</span><h3>Checklist para liberação das comissões</h3></div>
      <small>Assinatura: <b>{data.contract.subscription_status === 'active' ? 'Ativa' : 'Aguardando ativação'}</b></small>
    </div>
    {!data.contract.checklist_editable&&<div className="commission-checklist-locked"><strong>Checklist bloqueado</strong><span>Os itens poderão ser concluídos somente depois que a contratação estiver paga e a assinatura estiver ativa.</span></div>}

    {groups.map(group => <section key={group.role} className="commission-checklist-role">
      <header><div><h4>{roleDetails[group.role].title}</h4><span>{roleDetails[group.role].subtitle}</span></div></header>
      <div className="commission-checklist-milestones">
        {group.milestones.map(({ milestone, items, commission }) => {
          const completed = items.filter(item => item.completed_at).length;
          const progress = Math.round((completed / items.length) * 100);
          const paymentItem = items.find(item => item.is_payment_validation);
          const paymentStatus = paymentItem?.payment_status;
          return <article key={milestone} className={`commission-milestone ${completed === items.length ? 'is-complete' : ''}`}>
            <div className="commission-milestone-header">
              <div><span className="commission-milestone-number">{milestone}/3</span><div><strong>{milestone === 1 ? 'Primeiro marco' : milestone === 2 ? 'Segundo marco' : 'Terceiro marco'}</strong><small>{completed} de {items.length} itens concluídos</small></div></div>
              <div className="commission-milestone-value"><strong>{commission ? money(commission.amount) : 'Aguardando pagamento'}</strong><span>{statusLabels[commission?.status] || 'Comissão ainda não gerada'}</span>{paymentItem&&<em className={`commission-payment-status is-${paymentStatus||'waiting'}`}>{paymentLabels[paymentStatus]||'Aguardando geração da fatura'}</em>}</div>
            </div>
            <div className="commission-progress"><i style={{ width: `${progress}%` }} /></div>
            <div className="commission-checklist-items">
              {items.map(item => <button key={item.id} type="button" disabled={!item.can_toggle || updatingId === item.id} onClick={() => toggle(item)} className={item.completed_at ? 'is-checked' : ''}>
                <span className="commission-checkmark">{updatingId === item.id ? <Spinner animation="border" size="sm" /> : item.completed_at ? '✓' : ''}</span>
                <span><strong>{item.label}</strong>{item.is_payment_validation&&!item.completed_at&&<small>{paymentLabels[item.payment_status]||'Aguardando a fatura deste ciclo'}{item.payment_status==='paid'?' — clique para validar na Pagar.me':''}</small>}{item.completed_at && <small>Concluído por {item.is_payment_validation ? 'validação da Pagar.me' : (item.completed_by_name || 'responsável')} em {new Date(item.completed_at).toLocaleString('pt-BR')}</small>}</span>
              </button>)}
            </div>
            {completed === items.length && commission?.status === 'blocked' && <p className="commission-waiting-date">Checklist concluído. A comissão será liberada quando a data do marco chegar e a assinatura estiver ativa.</p>}
          </article>;
        })}
      </div>
    </section>)}
  </div>;
}